import { useEffect, useState } from "react";
import { Timer, Tag, Check, Trophy, Ban, Plus, Pencil, Download } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const emptyManual = { game_id: "", location_id: "", date: "", slot_id: "", customer_name: "", customer_email: "", customer_phone: "", num_players: 1, price_per_ticket: 0, total_amount: "", status_label: "Recorded", note: "" };

export default function BookingsAdmin() {
  const { user } = useAuth();
  const canEdit = user?.is_super || (user?.permissions || []).includes("bookings_edit");
  const [rows, setRows] = useState([]);
  const [comp, setComp] = useState(null);
  const [mins, setMins] = useState("");
  const [coup, setCoup] = useState(null);
  const [coupForm, setCoupForm] = useState({ coupon_code: "", reason: "" });
  const [games, setGames] = useState([]);
  const [locations, setLocations] = useState([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [m, setM] = useState(emptyManual);
  const [slotOpts, setSlotOpts] = useState([]);
  const [edit, setEdit] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = () => {
    const p = new URLSearchParams();
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    const qs = p.toString();
    return api.get(`/admin/bookings${qs ? `?${qs}` : ""}`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); api.get("/admin/games").then((r) => setGames(r.data)); api.get("/admin/locations").then((r) => setLocations(r.data)); }, []);

  useEffect(() => {
    if (m.game_id && m.location_id && m.date) {
      api.get(`/admin/slots?game_id=${m.game_id}&location_id=${m.location_id}&date=${m.date}`)
        .then((r) => setSlotOpts(r.data.filter((s) => !s.booking_id && s.status !== "blocked")));
    } else setSlotOpts([]);
  }, [m.game_id, m.location_id, m.date]);

  const mTotal = m.total_amount !== "" ? m.total_amount : Math.round((m.price_per_ticket || 0) * (m.num_players || 0));

  const saveComp = async () => {
    try {
      const { data } = await api.put(`/admin/bookings/${comp.booking_ref}/completion`, { completion_minutes: +mins });
      const lb = data.leaderboard;
      if (lb && lb.rank) toast.success(`Time saved · auto-added to leaderboard at rank #${lb.rank} of ${lb.total} 🏆`);
      else toast.success("Completion time saved");
      setComp(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const removeFromLb = async (b) => {
    try { await api.delete(`/admin/bookings/${b.booking_ref}/leaderboard`); toast.success("Removed from leaderboard"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toLeaderboard = async (b) => {
    try { const { data } = await api.post(`/admin/bookings/${b.booking_ref}/to-leaderboard`); const lb = data.leaderboard; toast.success(lb && lb.rank ? `Added to leaderboard at rank #${lb.rank} 🏆` : "Added to leaderboard 🏆"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveCoup = async () => {
    try { await api.put(`/admin/bookings/${coup.booking_ref}/coupon`, coupForm); toast.success("Updated"); setCoup(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const cancelBooking = async (b) => {
    if (!window.confirm(`Cancel booking ${b.booking_ref}? This frees the slot for re-sale. Customer details are kept in records.`)) return;
    try { await api.post(`/admin/bookings/${b.booking_ref}/cancel`); toast.success("Booking cancelled · slot freed"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveManual = async () => {
    if (!m.slot_id) return toast.error("Pick a slot to attach this booking to");
    try {
      await api.post("/admin/bookings/manual", {
        slot_id: m.slot_id, customer_name: m.customer_name, customer_email: m.customer_email, customer_phone: m.customer_phone,
        num_players: +m.num_players || 1, price_per_ticket: +m.price_per_ticket || 0,
        total_amount: m.total_amount === "" ? null : +m.total_amount, status_label: m.status_label, note: m.note,
      });
      toast.success("Manual booking saved · slot marked booked");
      setManualOpen(false); setM(emptyManual); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const openEdit = (b) => setEdit({ booking_ref: b.booking_ref, customer_name: b.customer_name || "", customer_email: b.customer_email || "", customer_phone: b.customer_phone || "", num_players: b.num_players || 1, price_per_ticket: b.price_per_ticket ?? "", total_amount: b.total_amount ?? "", status_label: b.payment_status || "", note: b.note || "" });
  const saveEdit = async () => {
    try {
      await api.put(`/admin/bookings/${edit.booking_ref}/details`, {
        customer_name: edit.customer_name, customer_email: edit.customer_email, customer_phone: edit.customer_phone,
        num_players: +edit.num_players || null, price_per_ticket: edit.price_per_ticket === "" ? null : +edit.price_per_ticket,
        total_amount: edit.total_amount === "" ? null : +edit.total_amount, status_label: edit.status_label, note: edit.note,
      });
      toast.success("Booking updated"); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const badge = (s) => s === "paid" ? "text-emerald-400" : s === "pay_at_venue" ? "text-amber-400" : s === "cancelled" ? "text-rose-400" : "text-slate-300";

  const clearRange = () => { setFrom(""); setTo(""); api.get("/admin/bookings").then((r) => setRows(r.data)); };
  const exportCsv = async () => {
    try {
      const p = new URLSearchParams();
      if (from) p.set("date_from", from);
      if (to) p.set("date_to", to);
      const qs = p.toString();
      const { data } = await api.get(`/admin/bookings/export${qs ? `?${qs}` : ""}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bookings${from ? `_${from}` : ""}${to ? `_${to}` : ""}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} booking${rows.length === 1 ? "" : "s"} to CSV`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const totalBookings = rows.length;
  const amountCollected = rows.filter((b) => b.payment_status !== "cancelled").reduce((s, b) => s + (b.total_amount || 0), 0);

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display font-extrabold text-3xl mb-1">Bookings</h1>
          <p className="text-slate-400">All online & manual bookings. Log the actual escape time — it's <b className="text-emerald-400">automatically added to the leaderboard</b>. Use <b className="text-violet-300">Add manual booking</b> to record offline/phone bookings against a slot.</p>
        </div>
        <Button onClick={() => { setM(emptyManual); setManualOpen(true); }} data-testid="add-manual-booking-btn" className={`bg-violet-600 hover:bg-violet-500 shrink-0 ${canEdit ? "" : "hidden"}`}><Plus className="h-4 w-4 mr-1" /> Add manual booking</Button>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4 rounded-2xl border border-white/10 bg-[#131a29] p-4">
        <div>
          <label className="text-xs text-slate-400 mb-1 block">From date</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="bookings-from" className="bg-slate-950/50 border-white/10 w-44" />
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">To date</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="bookings-to" className="bg-slate-950/50 border-white/10 w-44" />
        </div>
        <Button onClick={load} data-testid="bookings-apply-range" className="bg-violet-600 hover:bg-violet-500">Apply</Button>
        <Button onClick={clearRange} variant="secondary" data-testid="bookings-clear-range">Clear</Button>
        <Button onClick={exportCsv} variant="secondary" data-testid="bookings-export-csv"><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
        <div className="ml-auto flex gap-3">
          <div className="rounded-xl border border-white/10 bg-slate-950/40 px-5 py-2.5 text-center">
            <div className="text-xs text-slate-400">Total bookings{from || to ? " (range)" : ""}</div>
            <div className="font-display font-extrabold text-2xl" data-testid="summary-total-bookings">{totalBookings}</div>
          </div>
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-5 py-2.5 text-center">
            <div className="text-xs text-emerald-300/80">Amount collected</div>
            <div className="font-display font-extrabold text-2xl text-emerald-400" data-testid="summary-amount-collected">₹{amountCollected.toLocaleString("en-IN")}</div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#131a29] overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead className="bg-slate-950/50 text-slate-400 text-xs uppercase">
            <tr>{["Ref", "Game", "Location", "Date/Time", "Customer", "Players", "₹/Ticket", "Total", "Status", "Time", "Actions"].map((h) => <th key={h} className="text-left px-3 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`booking-${b.booking_ref}`}>
                <td className="px-3 py-3 font-mono text-violet-300">{b.booking_ref}{b.is_manual && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-slate-400">manual</span>}</td>
                <td className="px-3 py-3">{b.game_name}</td>
                <td className="px-3 py-3 text-slate-400">{b.location_name}</td>
                <td className="px-3 py-3 text-slate-400">{b.date} {b.start_time}</td>
                <td className="px-3 py-3">{b.customer_name || "—"}<div className="text-xs text-slate-500">{b.customer_phone}</div></td>
                <td className="px-3 py-3">{b.num_players}</td>
                <td className="px-3 py-3 font-mono text-slate-400">{b.price_per_ticket != null ? `₹${b.price_per_ticket}` : "—"}</td>
                <td className="px-3 py-3 font-mono">₹{b.total_amount}</td>
                <td className={`px-3 py-3 ${badge(b.payment_status)}`}>{b.payment_status}</td>
                <td className="px-3 py-3 font-mono text-emerald-400">{b.completion_minutes ? `${b.completion_minutes}m` : "—"}</td>
                <td className="px-3 py-3">{!canEdit ? <span className="text-xs text-slate-500">View only</span> : (
                  <div className="flex gap-1">
                    <Button size="sm" variant="secondary" onClick={() => openEdit(b)} data-testid={`edit-booking-${b.booking_ref}`} title="Edit customer details & price"><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="secondary" onClick={() => { setComp(b); setMins(b.completion_minutes || ""); }} data-testid={`set-time-${b.booking_ref}`}><Timer className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => { setCoup(b); setCoupForm({ coupon_code: b.coupon_code || "", reason: "" }); }} data-testid={`coupon-${b.booking_ref}`}><Tag className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" disabled={!b.completion_minutes}
                      className={b.leaderboard_added ? "text-emerald-400" : "text-slate-500"}
                      onClick={() => b.leaderboard_added ? removeFromLb(b) : toLeaderboard(b)}
                      data-testid={`to-lb-${b.booking_ref}`}
                      title={!b.completion_minutes ? "Log the escape time first — it auto-adds to the leaderboard" : b.leaderboard_added ? "On leaderboard (auto-ranked) · click to remove" : "Add to leaderboard"}>
                      <Trophy className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" disabled={b.payment_status === "cancelled"}
                      className="text-rose-400 hover:text-rose-300"
                      onClick={() => cancelBooking(b)}
                      data-testid={`cancel-booking-${b.booking_ref}`}
                      title={b.payment_status === "cancelled" ? "Already cancelled" : "Cancel booking · frees the slot for re-sale"}>
                      <Ban className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={11} className="px-3 py-10 text-center text-slate-500">No bookings yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Add manual booking */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="bg-[#131a29] border-white/10 max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add manual booking</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400 -mt-2 mb-1">Attach to a slot (marks it booked). All customer fields are optional — nothing touches payments.</p>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Game</label>
                <Select value={m.game_id} onValueChange={(v) => setM({ ...m, game_id: v, slot_id: "" })}>
                  <SelectTrigger data-testid="manual-game" className="bg-slate-950/50 border-white/10"><SelectValue placeholder="Select game" /></SelectTrigger>
                  <SelectContent>{games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Location</label>
                <Select value={m.location_id} onValueChange={(v) => setM({ ...m, location_id: v, slot_id: "" })}>
                  <SelectTrigger data-testid="manual-location" className="bg-slate-950/50 border-white/10"><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Date</label>
                <Input type="date" value={m.date} onChange={(e) => setM({ ...m, date: e.target.value, slot_id: "" })} data-testid="manual-date" className="bg-slate-950/50 border-white/10" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Slot</label>
                <Select value={m.slot_id} onValueChange={(v) => setM({ ...m, slot_id: v })} disabled={!slotOpts.length}>
                  <SelectTrigger data-testid="manual-slot" className="bg-slate-950/50 border-white/10"><SelectValue placeholder={slotOpts.length ? "Select slot" : "Pick game/loc/date"} /></SelectTrigger>
                  <SelectContent>{slotOpts.map((s) => <SelectItem key={s.id} value={s.id}>{s.start_time}–{s.end_time}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Input placeholder="Customer name" value={m.customer_name} onChange={(e) => setM({ ...m, customer_name: e.target.value })} data-testid="manual-name" className="bg-slate-950/50 border-white/10" />
              <Input placeholder="Phone" value={m.customer_phone} onChange={(e) => setM({ ...m, customer_phone: e.target.value })} data-testid="manual-phone" className="bg-slate-950/50 border-white/10" />
              <Input placeholder="Email" value={m.customer_email} onChange={(e) => setM({ ...m, customer_email: e.target.value })} data-testid="manual-email" className="bg-slate-950/50 border-white/10" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs text-slate-400 mb-1 block">People</label><Input type="number" value={m.num_players} onChange={(e) => setM({ ...m, num_players: e.target.value })} data-testid="manual-players" className="bg-slate-950/50 border-white/10" /></div>
              <div><label className="text-xs text-slate-400 mb-1 block">₹ / ticket</label><Input type="number" value={m.price_per_ticket} onChange={(e) => setM({ ...m, price_per_ticket: e.target.value })} data-testid="manual-ppt" className="bg-slate-950/50 border-white/10" /></div>
              <div><label className="text-xs text-slate-400 mb-1 block">Total ₹ (auto, editable)</label><Input type="number" value={mTotal} onChange={(e) => setM({ ...m, total_amount: e.target.value })} data-testid="manual-total" className="bg-slate-950/50 border-white/10" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Status (free text)" value={m.status_label} onChange={(e) => setM({ ...m, status_label: e.target.value })} data-testid="manual-status" className="bg-slate-950/50 border-white/10" />
              <Input placeholder="Note (optional)" value={m.note} onChange={(e) => setM({ ...m, note: e.target.value })} data-testid="manual-note" className="bg-slate-950/50 border-white/10" />
            </div>
          </div>
          <DialogFooter><Button onClick={saveManual} className="bg-violet-600 hover:bg-violet-500" data-testid="save-manual-booking"><Check className="h-4 w-4 mr-1" /> Save booking</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit booking details */}
      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="bg-[#131a29] border-white/10 max-w-xl">
          <DialogHeader><DialogTitle>Edit booking · {edit?.booking_ref}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <Input placeholder="Customer name" value={edit.customer_name} onChange={(e) => setEdit({ ...edit, customer_name: e.target.value })} data-testid="edit-name" className="bg-slate-950/50 border-white/10" />
                <Input placeholder="Phone" value={edit.customer_phone} onChange={(e) => setEdit({ ...edit, customer_phone: e.target.value })} data-testid="edit-phone" className="bg-slate-950/50 border-white/10" />
                <Input placeholder="Email" value={edit.customer_email} onChange={(e) => setEdit({ ...edit, customer_email: e.target.value })} data-testid="edit-email" className="bg-slate-950/50 border-white/10" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-xs text-slate-400 mb-1 block">People</label><Input type="number" value={edit.num_players} onChange={(e) => setEdit({ ...edit, num_players: e.target.value })} data-testid="edit-players" className="bg-slate-950/50 border-white/10" /></div>
                <div><label className="text-xs text-slate-400 mb-1 block">₹ / ticket</label><Input type="number" value={edit.price_per_ticket} onChange={(e) => setEdit({ ...edit, price_per_ticket: e.target.value })} data-testid="edit-ppt" className="bg-slate-950/50 border-white/10" /></div>
                <div><label className="text-xs text-slate-400 mb-1 block">Total ₹</label><Input type="number" value={edit.total_amount} onChange={(e) => setEdit({ ...edit, total_amount: e.target.value })} data-testid="edit-total" className="bg-slate-950/50 border-white/10" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Status (free text)" value={edit.status_label} onChange={(e) => setEdit({ ...edit, status_label: e.target.value })} data-testid="edit-status" className="bg-slate-950/50 border-white/10" />
                <Input placeholder="Note" value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} data-testid="edit-note" className="bg-slate-950/50 border-white/10" />
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} className="bg-violet-600 hover:bg-violet-500" data-testid="save-edit-booking"><Check className="h-4 w-4 mr-1" /> Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!comp} onOpenChange={() => setComp(null)}>
        <DialogContent className="bg-[#131a29] border-white/10">
          <DialogHeader><DialogTitle>Log completion time · {comp?.booking_ref}</DialogTitle></DialogHeader>
          <Input type="number" step="0.1" placeholder="Minutes taken" value={mins} onChange={(e) => setMins(e.target.value)} data-testid="completion-input" className="bg-slate-950/50 border-white/10" />
          <DialogFooter><Button onClick={saveComp} className="bg-violet-600 hover:bg-violet-500" data-testid="save-completion"><Check className="h-4 w-4 mr-1" /> Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!coup} onOpenChange={() => setCoup(null)}>
        <DialogContent className="bg-[#131a29] border-white/10">
          <DialogHeader><DialogTitle>Adjust coupon · {coup?.booking_ref}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Coupon code (blank to remove)" value={coupForm.coupon_code} onChange={(e) => setCoupForm({ ...coupForm, coupon_code: e.target.value.toUpperCase() })} className="bg-slate-950/50 border-white/10" />
            <Input placeholder="Reason (required for audit)" value={coupForm.reason} onChange={(e) => setCoupForm({ ...coupForm, reason: e.target.value })} data-testid="coupon-reason" className="bg-slate-950/50 border-white/10" />
          </div>
          <DialogFooter><Button onClick={saveCoup} className="bg-violet-600 hover:bg-violet-500" data-testid="save-coupon-adjust">Apply</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
